import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type Schemas } from "@/api/client";
import { errorMessage, unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { invalidateMeterReading } from "@/features/assets/meter-cache";
import { FormDialog } from "@/shared/components/form-dialog";
import { EmptyState, ErrorState, LoadingState } from "@/shared/components/page";
import { formatDateTime, formatQuantity } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Field, Textarea } from "@/shared/ui/form";
import { Table, Td, Th, Tr } from "@/shared/ui/primitives";

/** Auditable, cursor-paginated history including reset and voided readings. */
export function AssetMeterHistory({ meter }: { meter: Schemas["MeterSummary"] }): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const [correction, setCorrection] = useState<Schemas["ReadingOut"] | null>(null);
  const [reason, setReason] = useState("");
  const history = useQuery({
    queryKey: ["assets", "meter-history", meter.id, cursor],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/meters/{meter_id}/readings/history", {
          params: { path: { meter_id: meter.id }, query: { cursor, limit: 25 } },
        }),
      ),
    enabled: can("meters.meter.view"),
  });
  const voidReading = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/v1/meters/readings/{reading_id}/void", {
          params: { path: { reading_id: correction?.id ?? "" } },
          body: { reason: reason.trim() },
        }),
      ),
    onSuccess: async () => {
      await invalidateMeterReading(qc);
      setCorrection(null);
      setReason("");
    },
  });
  if (!can("meters.meter.view")) return <></>;
  return (
    <section
      className="min-w-0 space-y-3"
      aria-label={t("feedbackAssets.meterHistory", { name: meter.name })}
    >
      <h3 className="text-sm font-semibold">
        {t("feedbackAssets.meterHistory", { name: meter.name })}
      </h3>
      {history.isPending ? (
        <LoadingState />
      ) : history.isError ? (
        <ErrorState error={history.error} onRetry={() => void history.refetch()} />
      ) : history.data.items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>{t("feedbackAssets.timestamp")}</Th>
                <Th>{t("assets.readingValue")}</Th>
                <Th>{t("feedbackAssets.sourceLabel")}</Th>
                <Th>{t("feedbackAssets.recorder")}</Th>
                <Th>{t("app.notes")}</Th>
                <Th>{t("feedbackAssets.reset")}</Th>
                <Th>{t("feedbackAssets.void")}</Th>
                {can("meters.reading.void") ? <Th>{t("feedbackAssets.correction")}</Th> : null}
              </tr>
            </thead>
            <tbody>
              {history.data.items.map((reading) => (
                <Tr key={reading.id}>
                  <Td className="whitespace-nowrap">{formatDateTime(reading.read_at)}</Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {formatQuantity(reading.value)} {meter.unit}
                  </Td>
                  <Td>
                    {t(`feedbackAssets.sources.${reading.source}`, {
                      defaultValue: reading.source,
                    })}
                  </Td>
                  <Td>{reading.recorded_by_name ?? t("feedbackAssets.system")}</Td>
                  <Td className="min-w-40 max-w-80 whitespace-pre-wrap break-words">
                    {reading.note || "—"}
                  </Td>
                  <Td>{t(reading.is_reset ? "feedbackAssets.yes" : "feedbackAssets.no")}</Td>
                  <Td>{t(reading.is_void ? "feedbackAssets.yes" : "feedbackAssets.no")}</Td>
                  {can("meters.reading.void") ? (
                    <Td>
                      {!reading.is_void ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCorrection(reading);
                            setReason("");
                            voidReading.reset();
                          }}
                        >
                          {t("feedbackAssets.voidReading")}
                        </Button>
                      ) : null}
                    </Td>
                  ) : null}
                </Tr>
              ))}
            </tbody>
          </Table>
          <nav
            className="flex flex-wrap items-center justify-between gap-2"
            aria-label={t("feedbackAssets.historyPages")}
          >
            <Button
              variant="outline"
              disabled={cursors.length === 1 || history.isFetching}
              onClick={() => setCursors((pages) => pages.slice(0, -1))}
            >
              {t("feedbackAssets.previous")}
            </Button>
            <span className="text-sm">{t("feedbackAssets.page", { page: cursors.length })}</span>
            <Button
              variant="outline"
              disabled={!history.data.next_cursor || history.isFetching}
              onClick={() =>
                setCursors((pages) => [...pages, history.data.next_cursor ?? undefined])
              }
            >
              {t("feedbackAssets.next")}
            </Button>
          </nav>
        </>
      )}
      {correction ? (
        <FormDialog
          open
          onOpenChange={() => setCorrection(null)}
          title={t("feedbackAssets.voidReading")}
          submitLabel={t("feedbackAssets.voidReading")}
          submitting={voidReading.isPending}
          disabled={!reason.trim()}
          error={voidReading.isError ? errorMessage(voidReading.error) : undefined}
          onSubmit={async () => {
            if (reason.trim() && can("meters.reading.void")) await voidReading.mutateAsync();
          }}
        >
          <p className="text-sm">
            {t("feedbackAssets.voidConfirm", {
              value: formatQuantity(correction.value),
              date: formatDateTime(correction.read_at),
            })}
          </p>
          <Field id={`void-${meter.id}`} label={t("app.reason")}>
            <Textarea
              id={`void-${meter.id}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
            />
          </Field>
        </FormDialog>
      ) : null}
    </section>
  );
}
